import { ChevronDown, ChevronUp, Gift, GraduationCap } from "lucide-react";
import * as React from "react";

import type { Deal, DealListItem, DealOutcomeKind, RecentAsset } from "@/api/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { FeedCard } from "@/components/ui/feed-card";
import { cn } from "@/lib/utils";

import { DealExpiredContextCard } from "./DealExpiredContextCard";
import { DealExpiryDecisionOverlay } from "./DealExpiryDecisionOverlay";
import { DealOutcomeCard } from "./DealOutcomeCard";
import { DraftResumeCard } from "./DraftResumeCard";
import { ExpiryNotificationCard } from "./ExpiryNotificationCard";
import { NewCollateralCard } from "./NewCollateralCard";

/** Cap on collapsed cards — the feed is a glanceable task list, not a queue.
 * The "see additional cards" affordance expands the rest (CHP-320). */
const MAX_FEED_ITEMS = 5;

/** How long a decided deal keeps its outcome card in the feed. */
const OUTCOME_WINDOW_DAYS = 14;

/** Outcome kinds that demand a partner response (re-submit, dispute, move
 * on) — these sort into the action tier ahead of the good-news approvals. */
const ACTION_OUTCOME_KINDS = new Set<DealOutcomeKind>([
  "registration_declined",
  "extension_declined",
  "closed_won_declined",
]);

/**
 * Outcome alert cards derive from the partner's existing deal-list query:
 * recently-reviewed deals whose review landed within the last 14 days and
 * which the owner hasn't dismissed (CHP-304). Shared by the deals dashboard
 * and the partner main dashboard so the two feeds can't drift.
 *
 * The server stamps a `last_outcome` discriminator on each row
 * (`last_outcome_kind` in the api) so the card can name the exact outcome —
 * Registration / Extension (incl. amended) / Closed-Won, Approved / Declined —
 * instead of inferring from raw status. A null discriminator means "no reviewed
 * outcome to surface" and is filtered out here.
 */
export function deriveOutcomeCards(deals: DealListItem[]): DealListItem[] {
  const cutoff = Date.now() - OUTCOME_WINDOW_DAYS * 24 * 60 * 60 * 1000;
  return deals.filter(
    (d) =>
      d.last_outcome != null &&
      d.reviewed_at != null &&
      new Date(d.reviewed_at).getTime() >= cutoff &&
      d.outcome_dismissed_at === null,
  );
}

/** Whether an outcome card belongs to the action tier (declines/rejections)
 * rather than the deprioritised informational tier (approvals). */
export function isActionOutcome(deal: DealListItem): boolean {
  return deal.last_outcome != null && ACTION_OUTCOME_KINDS.has(deal.last_outcome);
}

/**
 * Post-expiry "DEAL EXPIRED — CLOSED-LOST" cards (CHP-380): deals that
 * auto-expired (LIVE→CLOSED_LOST via the SLA sweep) and have no close reason
 * recorded yet. The owner records the reason via the card's inline "Add
 * context" form; once added (expiry_context_added_at set) the card drops off.
 */
export function deriveExpiredContextCards(deals: DealListItem[]): DealListItem[] {
  return deals.filter(
    (d) =>
      d.deal_status === "closed_lost" &&
      d.auto_expired === true &&
      (d.expiry_context_added_at ?? null) === null &&
      // CHP-599: [X]-dismissals are persisted server-side, so a card dismissed
      // on either partner dashboard stays gone on both (and across reloads).
      (d.expired_context_dismissed_at ?? null) === null,
  );
}

export interface DealFeedSidebarProps {
  /** Live deals approaching expiry (filtered + supplied by the route). */
  expiring: Deal[];
  /** Resumable drafts. */
  drafts: Deal[];
  /** Recently decided deals (see deriveOutcomeCards). */
  outcomes: DealListItem[];
  /** Auto-expired deals awaiting a close reason (CHP-380, see
   * deriveExpiredContextCards). */
  expiredContext: DealListItem[];
  /** When set, the matching expiring card expands INLINE into the expiry
   * decision overlay (CHP-388); the rest of the feed stays visible. */
  reviewingDeal: Deal | null;
  onReviewOptions: (dealId: string) => void;
  onCloseReview: () => void;
  onResumeDraft: (draftId: string) => void;
  onDiscardDraft: (dealId: string) => void;
  isDiscarding: boolean;
  /** Expiring-card [X] — acknowledges expiry WITHOUT opening the overlay. */
  onDismissExpiring: (dealId: string) => void;
  /** Draft-card [X] — hides the card (draft_dismissed_at); never deletes. */
  onDismissDraft: (dealId: string) => void;
  /** Outcome-card [X] — hides the card (outcome_dismissed_at); never deletes. */
  onDismissOutcome: (dealId: string) => void;
  /** Outcome-card [View deal] — opens the connected deal detail (CHP-304). */
  onViewOutcome: (dealId: string) => void;
  /** CHP-465: count of outstanding compulsory modules. >0 surfaces a training
   * card pinned above the cap (so the prompt is never clipped). Defaults to 0
   * (no card) so existing callers without training context are unaffected. */
  outstandingTraining?: number;
  /** CHP-465: training-card CTA — opens the training hub. Required for the card
   * to render. */
  onOpenTraining?: () => void;
  /** CHP-464 (finishing CHP-387): LIVE deals past the 45-day midpoint with no
   * claimed first-meeting reward, for a DIRECT-payout viewer (the route applies
   * the payout gate + `isMeetingRewardReminderDue`). Each renders a "First
   * meeting reward status" action card with [Claim reward] | [Dismiss].
   * Defaults to [] so callers without a rewards context are unaffected. */
  meetingRewards?: DealListItem[];
  /** Opens the claim modal for the deal. Required for the cards to render. */
  onClaimMeetingReward?: (dealId: string) => void;
  /** Dismiss — stamps meeting_reward_reminder_stopped_at (the one-time Dismiss
   * marker), so the card never re-surfaces. Required for the cards to render. */
  onDismissMeetingReward?: (dealId: string) => void;
  /** follow-up #62: recently-added sales collateral. Each renders a "New Sales
   * Collateral Added" info-tier card (below the deadline/action cards).
   * Defaults to [] so callers without an assets context are unaffected. */
  recentAssets?: RecentAsset[];
  /** follow-up #62: asset-card [View / Download] — opens the asset / Assets
   * Hub. Required for the asset cards to render. */
  onViewAsset?: (assetId: string) => void;
}

type FeedItem =
  | { kind: "expiring"; deal: Deal }
  | { kind: "draft"; deal: Deal }
  | { kind: "outcome"; deal: DealListItem }
  | { kind: "expired_context"; deal: DealListItem }
  | { kind: "meeting_reward"; deal: DealListItem }
  | { kind: "asset"; asset: RecentAsset };

/** Card-exit animation duration (ms). Matches the CSS transition below; kept
 * short (spec 719224884: "not perceptible as slow", 150–200ms). */
const DISMISS_ANIM_MS = 180;

/**
 * Wraps a feed card so dismissing it animates out (fade + shift-up + collapse)
 * before the underlying record actually drops off the feed (spec 719224884:
 * "cards shift up when an item is dismissed"). No animation library is used —
 * a CSS transition on a per-card `dismissing` flag, the codebase-idiomatic
 * approach (no framer-motion dependency).
 *
 * The real dismiss callback (which fires the non-destructive mutation and lets
 * the parent query re-flow the card away) is deferred until the exit transition
 * settles — `onTransitionEnd` fires it, with a `DISMISS_ANIM_MS` timeout as the
 * fallback for environments where the transition event never fires (jsdom).
 * A guard ensures it fires exactly once. Initial render carries no enter
 * animation. `motion-reduce` collapses the transition for reduced-motion users.
 */
function FeedCardShell({
  children,
  onDismiss,
}: {
  /** Render-prop: receives the wrapped dismiss handler to wire to the card's
   * [X]. Calling it starts the exit animation rather than dismissing instantly. */
  children: (beginDismiss: () => void) => React.ReactNode;
  /** The real dismiss handler (mutation + re-flow), called once the exit
   * animation has played. */
  onDismiss: () => void;
}) {
  const [dismissing, setDismissing] = React.useState(false);
  const firedRef = React.useRef(false);

  const fireOnce = React.useCallback(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    onDismiss();
  }, [onDismiss]);

  const beginDismiss = React.useCallback(() => {
    if (dismissing) return;
    setDismissing(true);
    // Fallback for jsdom / no-transition environments where transitionend
    // never fires. fireOnce() is idempotent so a later transitionend is a no-op.
    window.setTimeout(fireOnce, DISMISS_ANIM_MS + 20);
  }, [dismissing, fireOnce]);

  return (
    <div
      onTransitionEnd={dismissing ? fireOnce : undefined}
      className={cn(
        "overflow-hidden transition-all duration-200 ease-out motion-reduce:transition-none",
        dismissing ? "max-h-0 -translate-y-2 opacity-0" : "max-h-[40rem] translate-y-0 opacity-100",
      )}
    >
      {children(beginDismiss)}
    </div>
  );
}

/**
 * "Your Feed": priority-ordered to-do/outcome cards. Action cards first —
 * soonest-expiring deals, decline/rejection outcomes, resumable drafts —
 * then deprioritised informational cards (approval outcomes; future
 * nice-to-know cards join the info tier). Rendered full-width above the deal
 * workspace (RACCPE-17). Returns `null` when there's nothing to show so the
 * route omits the section entirely.
 *
 * Collapsed to the first five cards by default; the "see additional cards"
 * affordance expands the feed to reveal the rest and collapses it again
 * (CHP-320). Every card carries its own non-destructive [X] (expiring →
 * acknowledge-expiry, draft → draft_dismissed_at, outcome →
 * outcome_dismissed_at) so any card can be taken off the feed.
 */
export function DealFeedSidebar({
  expiring,
  drafts,
  outcomes,
  expiredContext,
  reviewingDeal,
  onReviewOptions,
  onCloseReview,
  onResumeDraft,
  onDiscardDraft,
  isDiscarding,
  onDismissExpiring,
  onDismissDraft,
  onDismissOutcome,
  onViewOutcome,
  outstandingTraining = 0,
  onOpenTraining,
  meetingRewards = [],
  onClaimMeetingReward,
  onDismissMeetingReward,
  recentAssets = [],
  onViewAsset,
}: DealFeedSidebarProps) {
  const [expanded, setExpanded] = React.useState(false);
  // follow-up #62: there is no server-side dismissal for "new collateral"
  // cards (no per-user seen-marker), so an asset-card [X] is a client-side
  // session dismiss tracked here.
  const [dismissedAssetIds, setDismissedAssetIds] = React.useState<ReadonlySet<string>>(
    () => new Set(),
  );
  // Review-r2 (conventions sweep): the pinned training card must be closable
  // like every other feed card. There's no server-side dismissal for it (the
  // banner/metric still surface the outstanding training), so the [X] is a
  // client-side session dismiss tracked here — same pattern as the asset cards.
  const [trainingDismissed, setTrainingDismissed] = React.useState(false);

  // BLOCKER fix (review-r2 40fea763 / 4efaa85d): server-backed dismissals
  // (expiring / draft / outcome) fire their mutation only after the exit
  // animation settles, then wait on a query refetch to drop the row. In that
  // gap the source arrays still carry the card, so `totalCount`, the
  // first-five slice, and the "See N more" toggle all disagreed with what was
  // on screen — symptom: "shows 6 left but none appear, references 1 more
  // card", and the last hidden card could never be reached/closed. Track an
  // optimistic dismissed-id set HERE and filter every source array through it
  // before counting/slicing, so the moment a card's real dismiss fires it
  // leaves the model in lock-step with the screen.
  const [dismissedIds, setDismissedIds] = React.useState<ReadonlySet<string>>(() => new Set());
  const markDismissed = React.useCallback((id: string) => {
    setDismissedIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);
  const notDismissed = React.useCallback(
    (d: { id: string }) => !dismissedIds.has(d.id),
    [dismissedIds],
  );

  // Each real dismiss both fires the parent mutation AND removes the card from
  // the local model immediately (keeps counts/slice consistent through the
  // refetch window).
  const handleDismissExpiring = (id: string) => {
    markDismissed(id);
    onDismissExpiring(id);
  };
  const handleDismissDraft = (id: string) => {
    markDismissed(id);
    onDismissDraft(id);
  };
  const handleDismissOutcome = (id: string) => {
    markDismissed(id);
    onDismissOutcome(id);
  };
  const handleDismissMeetingReward = (id: string) => {
    markDismissed(id);
    onDismissMeetingReward?.(id);
  };

  // CHP-465: surface outstanding compulsory training as a feed card (the banner
  // + metric already show it; the feed didn't). Pinned above the deal-card cap.
  const showTraining = outstandingTraining > 0 && !!onOpenTraining && !trainingDismissed;
  // follow-up #62: asset cards render only when a CTA handler is wired and the
  // card hasn't been session-dismissed.
  const visibleAssets =
    onViewAsset != null ? recentAssets.filter((a) => !dismissedAssetIds.has(a.id)) : [];

  // Filter the server-backed source arrays through the optimistic set so a
  // just-dismissed card is gone from the model before its refetch lands.
  const visibleExpiring = expiring.filter(notDismissed);
  const visibleDrafts = drafts.filter(notDismissed);
  const visibleOutcomes = outcomes.filter(notDismissed);
  // CHP-1044: expired-context cards report their session dismiss up via
  // onDismiss → markDismissed, so they flow through the same optimistic set
  // and the badge/slice decrement the moment the [X] is clicked.
  const visibleExpiredContext = expiredContext.filter(notDismissed);
  // CHP-464: reward cards render only when both handlers are wired (claim
  // needs the modal, dismiss needs the stop mutation).
  const visibleMeetingRewards =
    onClaimMeetingReward && onDismissMeetingReward ? meetingRewards.filter(notDismissed) : [];

  const totalCount =
    visibleExpiring.length +
    visibleDrafts.length +
    visibleOutcomes.length +
    visibleExpiredContext.length +
    visibleMeetingRewards.length +
    visibleAssets.length +
    (showTraining ? 1 : 0);

  // Soonest close date first (ascending target_close_date == ascending
  // days remaining); nulls sort last.
  const sortedExpiring = [...visibleExpiring].sort((a, b) =>
    (a.target_close_date ?? "9999-12-31").localeCompare(b.target_close_date ?? "9999-12-31"),
  );

  // Declines/rejections demand a response (re-submit, dispute, move on) —
  // action tier. Approvals are good news only — informational tier, below.
  const deniedOutcomes = visibleOutcomes.filter(isActionOutcome);
  const approvedOutcomes = visibleOutcomes.filter((d) => !isActionOutcome(d));

  const actionItems: FeedItem[] = [
    // CHP-380: expired deals awaiting a close reason lead the action tier.
    ...visibleExpiredContext.map((deal): FeedItem => ({ kind: "expired_context", deal })),
    ...sortedExpiring.map((deal): FeedItem => ({ kind: "expiring", deal })),
    ...deniedOutcomes.map((deal): FeedItem => ({ kind: "outcome", deal })),
    ...visibleDrafts.map((deal): FeedItem => ({ kind: "draft", deal })),
    // CHP-464: reward nudges close the action tier — actionable, but never
    // ahead of deadline/decline cards.
    ...visibleMeetingRewards.map((deal): FeedItem => ({ kind: "meeting_reward", deal })),
  ];
  const infoItems: FeedItem[] = [
    ...approvedOutcomes.map((deal): FeedItem => ({ kind: "outcome", deal })),
    // follow-up #62: "New Sales Collateral Added" cards sit in the info tier,
    // below the deadline/action cards.
    ...visibleAssets.map((asset): FeedItem => ({ kind: "asset", asset })),
  ];
  const allItems = [...actionItems, ...infoItems];
  // Collapsed: first five. Expanded: everything (CHP-320).
  const items = expanded ? allItems : allItems.slice(0, MAX_FEED_ITEMS);
  const hiddenCount = allItems.length - MAX_FEED_ITEMS;

  // CHP-1059: the "Review options" deep-link (?reviewDealId) must always land
  // ON the inline expiry overlay. If the target expiring card sits behind the
  // "See N more cards" clip, auto-expand so the overlay can render at all…
  const reviewingClipped =
    reviewingDeal != null &&
    allItems.findIndex((it) => it.kind === "expiring" && it.deal.id === reviewingDeal.id) >=
      MAX_FEED_ITEMS;
  React.useEffect(() => {
    // Deliberate: a manual "Show fewer" while reviewing stays collapsed (the
    // boolean hasn't changed, so this doesn't re-fire) — user intent wins.
    if (reviewingClipped) setExpanded(true);
  }, [reviewingClipped]);
  // …and scroll the overlay into view once it has mounted (next tick — the
  // expansion re-render must land first). Optional-call: jsdom has no
  // scrollIntoView.
  const reviewingId = reviewingDeal?.id ?? null;
  const overlayRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!reviewingId) return undefined;
    const t = window.setTimeout(() => {
      overlayRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    }, 0);
    return () => window.clearTimeout(t);
  }, [reviewingId]);

  // Review-r2 (conventions sweep): an empty feed renders a calm zero-state
  // message rather than collapsing to nothing, so the section always reads as
  // "your feed" even when there's nothing to action. CHP-887: the "Your Feed"
  // heading renders in this branch too (it was previously missing in the empty
  // state). (Kept BELOW the CHP-1059 hooks — rules of hooks.)
  if (totalCount === 0) {
    return (
      <aside className="space-y-3" aria-label="Your feed">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold text-muted-foreground">Your Feed</h2>
        </div>
        <Card data-testid="feed-empty">
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            {/* CHP-893: the zero-state copy is training-aware — until mandatory
                training is complete it names training as a precondition. */}
            {outstandingTraining > 0
              ? "Nothing needs your attention. Once you have completed training and a deal is registered updates will appear here."
              : "Nothing needs your attention. Once a deal is registered updates will appear here."}
          </CardContent>
        </Card>
      </aside>
    );
  }

  return (
    <aside className="space-y-3" aria-label="Your feed">
      <div className="flex items-center gap-2">
        <h2 className="text-sm font-semibold text-muted-foreground">Your Feed</h2>
        <Badge variant="secondary" data-testid="feed-count">
          {totalCount}
        </Badge>
      </div>

      {/* CHP-388: the expiry decision overlay expands INLINE in place of the
          card the partner clicked "Review options" on — the rest of the feed
          stays visible and just drops lower. (Previously the overlay replaced
          the whole feed.) `reviewingDeal` always resolves to an expiring item,
          so the matching expiring card swaps to the overlay. */}
      <div className="grid gap-3">
        {showTraining ? (
          // CHP-465 / CHP-523: outstanding compulsory training as a feed card.
          // CHP-877: mandatory training is urgent → the red lane (was amber).
          <FeedCard
            tone="red"
            icon={GraduationCap}
            // CHP-880 + CHP-881 (merged): eyebrow from 881, title from 880,
            // body count from 881.
            eyebrow="Training required to register deals"
            rawTestId="bdm-feed-training"
            onDismiss={() => setTrainingDismissed(true)}
            dismissLabel="Dismiss training reminder"
            dismissTestId="bdm-feed-training-dismiss"
            title="Complete this compulsory module to unlock deal registration"
            description={`${outstandingTraining} module${
              outstandingTraining === 1 ? "" : "s"
            } outstanding`}
            action={
              <Button size="sm" onClick={onOpenTraining} data-testid="bdm-feed-open-training">
                Complete training
              </Button>
            }
          />
        ) : null}
        {items.map((item) =>
          item.kind === "expiring" ? (
            reviewingDeal && reviewingDeal.id === item.deal.id ? (
              // The inline decision overlay is not a dismissible card — render
              // it directly (no exit-animation shell). CHP-1059: the wrapper
              // ref lets the deep-link effect scroll the overlay into view.
              <div key={item.deal.id} ref={overlayRef}>
                <DealExpiryDecisionOverlay deal={reviewingDeal} onClose={onCloseReview} />
              </div>
            ) : (
              <FeedCardShell
                key={item.deal.id}
                onDismiss={() => handleDismissExpiring(item.deal.id)}
              >
                {(beginDismiss) => (
                  <ExpiryNotificationCard
                    deal={item.deal}
                    onManageExpiry={() => onReviewOptions(item.deal.id)}
                    onDismiss={beginDismiss}
                  />
                )}
              </FeedCardShell>
            )
          ) : item.kind === "draft" ? (
            <FeedCardShell key={item.deal.id} onDismiss={() => handleDismissDraft(item.deal.id)}>
              {(beginDismiss) => (
                <DraftResumeCard
                  deal={item.deal}
                  onResume={() => onResumeDraft(item.deal.id)}
                  onDiscard={() => onDiscardDraft(item.deal.id)}
                  isDiscarding={isDiscarding}
                  onDismiss={beginDismiss}
                />
              )}
            </FeedCardShell>
          ) : item.kind === "expired_context" ? (
            // The expired-context card keeps its internal [X] (returns null
            // instantly, un-animated — not wrapped in FeedCardShell; revisit
            // for animation parity if the designer wants it, 6.5 follow-up).
            // CHP-1044: it now ALSO reports the dismiss up into the optimistic
            // set so the feed badge decrements in real time.
            <DealExpiredContextCard
              key={item.deal.id}
              deal={item.deal}
              onDismiss={() => markDismissed(item.deal.id)}
            />
          ) : item.kind === "meeting_reward" ? (
            // CHP-464 (finishing CHP-387): direct-payout first-meeting-reward
            // midpoint card. Footer carries the spec'd [Claim reward] |
            // [Dismiss]; Dismiss (and the [X]) stop reminders for this deal.
            <FeedCardShell
              key={`reward-${item.deal.id}`}
              onDismiss={() => handleDismissMeetingReward(item.deal.id)}
            >
              {(beginDismiss) => (
                <FeedCard
                  tone="info"
                  icon={Gift}
                  eyebrow="First meeting reward status"
                  rawTestId={`bdm-feed-meeting-reward-${item.deal.id}`}
                  title={item.deal.client_name}
                  description={
                    <div className="space-y-1">
                      {item.deal.deal_id_display ? <div>{item.deal.deal_id_display}</div> : null}
                      <p>
                        This deal has reached the 45-day midpoint and the first meeting reward has
                        not been claimed. Claim it now, or dismiss to stop these reminders.
                      </p>
                    </div>
                  }
                  onDismiss={beginDismiss}
                  dismissLabel={`Dismiss first meeting reward status for ${item.deal.client_name}`}
                  action={
                    <div className="flex flex-col gap-2">
                      <Button
                        size="sm"
                        onClick={() => onClaimMeetingReward?.(item.deal.id)}
                        data-testid={`bdm-feed-meeting-reward-claim-${item.deal.id}`}
                      >
                        <Gift className="h-3.5 w-3.5" aria-hidden />
                        Claim reward
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={beginDismiss}
                        data-testid={`bdm-feed-meeting-reward-dismiss-${item.deal.id}`}
                      >
                        Dismiss
                      </Button>
                    </div>
                  }
                />
              )}
            </FeedCardShell>
          ) : item.kind === "asset" ? (
            <FeedCardShell
              key={`asset-${item.asset.id}`}
              onDismiss={() => setDismissedAssetIds((prev) => new Set(prev).add(item.asset.id))}
            >
              {(beginDismiss) => (
                <NewCollateralCard
                  asset={item.asset}
                  onView={() => onViewAsset?.(item.asset.id)}
                  onDismiss={beginDismiss}
                />
              )}
            </FeedCardShell>
          ) : (
            <FeedCardShell key={item.deal.id} onDismiss={() => handleDismissOutcome(item.deal.id)}>
              {(beginDismiss) => (
                <DealOutcomeCard
                  deal={item.deal}
                  onDismiss={beginDismiss}
                  onViewDeal={() => onViewOutcome(item.deal.id)}
                />
              )}
            </FeedCardShell>
          ),
        )}
        {hiddenCount > 0 && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            data-testid="feed-expand-toggle"
            className="flex items-center justify-center gap-1 rounded-md py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
          >
            {expanded ? (
              <>
                <ChevronUp className="h-3.5 w-3.5" aria-hidden />
                Show fewer
              </>
            ) : (
              <>
                <ChevronDown className="h-3.5 w-3.5" aria-hidden />
                See {hiddenCount} more {hiddenCount === 1 ? "card" : "cards"}
              </>
            )}
          </button>
        )}
      </div>
    </aside>
  );
}
